import React from 'react';
import { Control, FieldPath, useController, useFormContext } from 'react-hook-form';

import { useTranslations } from '@lang';

import { useIsDesktop } from '@common/hooks/useIsDesktop';
import { getLimitArray } from '@utils/getLimitArray';

import { byPrice } from '@utils/sorters';
import { Option, Price, StockCheck } from 'shared-types/Common';

import Error from '@common/components/Error';
import { BlockSelectField } from '@common/components/FormElements/BlockSelectField';
import { PureSelectProps } from '@common/components/FormElements/BlockSelectField/BlockSelectField';
import { OptionPicker } from '@common/components/FormElements/OptionPicker';
import { RHFBlockSelectField } from '@common/components/FormElements/RHFBlockSelectField';
import Portal from '@common/components/Portal';
import { Product } from 'shared-types/Product';
import { AmountPicker } from './AmountPicker';
import { OptionsButton, OptionsButtonProps } from './OptionsButton';
import { QuantityPicker } from './QuantityPicker';

import { DiscountPrice } from '@common/components/Price';
import {
  ButtonContainer,
  ComingSoonButton,
  OptionsContainer,
  OptionsSelectWrapper,
  Row,
  SubmitButton,
} from './AddToBasketForm.styles';

import { AddToBasketFormValues } from './AddToBasketForm.types';

const SEARCHABLE_THRESHOLD = 10;

type ReactSelectPassthroughProps<TOption = PureSelectProps['options'][number]> = {
  getOptionLabel?: (option: TOption) => React.ReactNode;
  getOptionValue?: (option: TOption) => string | number;
};

type BlockSelectFieldWithReactSelectProps = PureSelectProps & ReactSelectPassthroughProps;

type RHFBlockSelectFieldExtendedProps = Omit<
  React.ComponentProps<typeof RHFBlockSelectField>,
  'control'
> &
  ReactSelectPassthroughProps & {
    // Control is invariant in RHF — Control<AddToBasketFormValues> is not
    // assignable to Control<FieldValues>. Accept any form shape here.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    control?: Control<any>;
  };

export type AddToBasketFormProps = {
  purchaseLimit: number | null;
  priceToDisplay: {
    price: Price;
  };
  hideOptionLabel: boolean;
  isMonetary?: boolean;
  selectedOption?: Option;
  optionsFieldName?: string;
  item: Product;
  stockRemaining?: StockCheck;
  onSubmit: (data: AddToBasketFormValues) => void;
} & Pick<OptionsButtonProps, 'id'>;

function getItemLimit(
  quantity: number | undefined,
  limit: number | null | undefined
): number | null {
  if (limit == null) return null;

  if (!quantity || quantity > limit) {
    return limit;
  }

  return quantity;
}

export const AddToBasketFormComponent: React.FC<AddToBasketFormProps> = ({
  purchaseLimit,
  id,
  priceToDisplay,
  hideOptionLabel,
  isMonetary,
  selectedOption,
  optionsFieldName = 'option',
  item,
  stockRemaining,
  onSubmit,
}) => {
  const { t } = useTranslations();
  const isDesktop = useIsDesktop();

  const { handleSubmit, setValue, control, watch, formState } =
    useFormContext<AddToBasketFormValues>();

  const amount = watch('amount');

  const { options } = item;
  const optionCount = options?.length ?? 0;
  const requiresOptionSelection = isMonetary || optionCount > 1;

  const optionField = useController({
    name: optionsFieldName as FieldPath<AddToBasketFormValues>,
    control,
    rules: requiresOptionSelection ? { required: 'optionRequired' } : undefined,
  });

  const quantityField = useController({
    name: 'quantity',
    control,
  });

  const onOptionChange = (option: Option) => {
    optionField.field.onChange(option);
    setValue('quantity', option.quantity === 0 ? 0 : 1);
  };

  const monetaryPrice = amount && {
    ...item.price,
    amount: amount * 100,
  };

  const optionsPrice = isMonetary ? monetaryPrice : priceToDisplay.price;

  const selectedStockCheck = stockRemaining?.options.find(
    (item) => item.option.id === selectedOption?.id
  );

  const isOptionUnavailable = selectedStockCheck?.availableQuantity === 0;
  const limit = getItemLimit(selectedStockCheck?.availableQuantity, purchaseLimit);

  const addToBasketDisabled =
    limit === 0 || isOptionUnavailable || stockRemaining?.availableQuantity === 0;

  const optionsMapFilter = (option: Option, i: number) => {
    const stock = stockRemaining?.options[i];
    const availableQuantity = stock?.availableQuantity;
    return {
      ...option,
      availableQuantity,
      isDisabled: availableQuantity === 0,
      value: option.id,
    };
  };

  const maximumVisibleOptions = 10;
  const quantityOptions = isOptionUnavailable
    ? [{ value: '0' }]
    : getLimitArray(limit != null && limit > maximumVisibleOptions ? maximumVisibleOptions : limit);

  const left = t('product.remaining');

  const formError = formState.errors?.root?.message;

  const onFormSubmit = handleSubmit(onSubmit);

  return (
    <>
      <form onSubmit={onFormSubmit} id={id}>
        {formError && (
          <Row>
            <Error error={formError} />
          </Row>
        )}
        <OptionsContainer data-testid="getAddToBasketContainer">
          {!isMonetary && (
            <OptionsSelectWrapper>
              {options && options.length > 1 && (
                <>
                  {isDesktop ? (
                    <BlockSelectField
                      {...({
                        options: options.map(optionsMapFilter).sort(byPrice),
                        getOptionLabel: (option) =>
                          concatenateOptionLabel(option, left, isMonetary),
                        getOptionValue: (option) => option.id,
                        isSearchable: options && options.length > SEARCHABLE_THRESHOLD,
                        label: hideOptionLabel ? t('product.select.option') : undefined,
                        placeholder: t('product.selectPlaceholder'),
                        value: optionField.field.value,
                        onChange: onOptionChange,
                        error: optionField.fieldState.error
                          ? optionField.fieldState.error.message
                          : undefined,
                      } as BlockSelectFieldWithReactSelectProps)}
                    />
                  ) : (
                    <>
                      <OptionPicker
                        options={options.map(optionsMapFilter).sort(byPrice)}
                        getOptionLabel={(option) =>
                          concatenateOptionLabel(option, left, isMonetary)
                        }
                        value={optionField.field.value}
                        onChange={onOptionChange}
                        label={hideOptionLabel ? t('product.select.option') : ''}
                      />
                      {optionField.fieldState.error && (
                        <Error error={optionField.fieldState.error.message} />
                      )}
                    </>
                  )}
                </>
              )}
            </OptionsSelectWrapper>
          )}
          {isMonetary && (
            <AmountPicker
              disabled={limit === 0}
              options={(options || []).sort(byPrice)}
              label={t('select.amount')}
              value={optionField.field.value}
              onChange={onOptionChange}
              error={
                optionField.fieldState.error ? optionField.fieldState.error.message : undefined
              }
            />
          )}
          {item.purchaseLimit === 0 ? (
            <ButtonContainer>
              <ComingSoonButton>{t('product.comingSoon')}</ComingSoonButton>
            </ButtonContainer>
          ) : (
            <ButtonContainer>
              {isDesktop ? (
                <RHFBlockSelectField
                  {...({
                    name: 'quantity',
                    control,
                    getOptionLabel: (option) => option.value,
                    getOptionValue: (option) => option.value,
                    isCreatable: true,
                    isSearchable: true,
                    options: quantityOptions,
                    placeholder: t('product.select.quantity'),
                    parse: (selected) => {
                      const val = selected?.value;
                      return !isNaN(val) ? parseInt(val, 10) : 1;
                    },
                    format: (value) =>
                      value != null ? { value: String(value), label: String(value) } : null,
                    isDisabled: Boolean(
                      limit === 0 ||
                        (options && options.length > 1 && !selectedOption) ||
                        addToBasketDisabled
                    ),
                  } as RHFBlockSelectFieldExtendedProps)}
                />
              ) : (
                <QuantityPicker
                  value={quantityField.field.value}
                  onChange={quantityField.field.onChange}
                  placeholder={t('product.select.quantity')}
                  maxValue={limit ?? undefined}
                  isDisabled={Boolean(
                    limit === 0 ||
                      (options && options.length > 1 && !selectedOption) ||
                      addToBasketDisabled
                  )}
                />
              )}
              {
                <SubmitButton isPrimary isInline type="submit" disabled={addToBasketDisabled}>
                  {t('product.ctaButton')}
                </SubmitButton>
              }
            </ButtonContainer>
          )}
        </OptionsContainer>
        <Portal selector="#options-button">
          <OptionsButton
            id={id}
            price={optionsPrice as Price}
            isSingleOption={Boolean(options && options.length < 2)}
            onFormSubmit={onFormSubmit}
            discount={item.discount}
          />
        </Portal>
      </form>
    </>
  );
};

const concatenateOptionLabel = (
  {
    quantity,
    availableQuantity = 0,
    name,
    price,
    discount,
  }: Option & { availableQuantity?: number },
  leftString: string,
  isMonetary?: boolean
) => {
  const quantityString = !quantity ? '' : `(${availableQuantity} ${leftString})`;
  return isMonetary ? (
    name
  ) : (
    <>
      {name}
      {' - '}
      <DiscountPrice price={price} discount={discount} /> {quantityString}
    </>
  );
};

export const AddToBasketForm = AddToBasketFormComponent;
